<template>
  <v-card class="mt-5" outlined>
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2" color="primary">mdi-briefcase-outline</v-icon>
      <span>Field Orders</span>
      <v-spacer />
      <v-btn color="primary" class="mr-2" small @click="openOrderDialog()">
        <v-icon left small>mdi-plus</v-icon>
        New Field Order
      </v-btn>
      <v-btn
        color="primary"
        class="mr-2"
        small
        outlined
        @click="openTypesDialog"
      >
        <v-icon left small>mdi-palette</v-icon>
        Job Types
      </v-btn>
      <v-btn icon @click="refreshAll" :loading="loading">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text>
      <!-- Period (by scheduled date) — drives the summary + list tabs, not the Calendar -->
      <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px">
        <v-btn-toggle
          v-model="periodMode"
          mandatory
          dense
          color="primary"
        >
          <v-btn small value="all">All</v-btn>
          <v-btn small value="week">Week</v-btn>
          <v-btn small value="month">Month</v-btn>
          <v-btn small value="range">Range</v-btn>
        </v-btn-toggle>
        <template v-if="periodMode === 'week' || periodMode === 'month'">
          <v-btn icon small @click="shiftPeriod(-1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="body-2 font-weight-medium">{{ periodLabel }}</span>
          <v-btn icon small @click="shiftPeriod(1)">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <v-btn text small color="primary" @click="periodAnchor = todayYmd()">
            {{ periodMode === "week" ? "This week" : "This month" }}
          </v-btn>
        </template>
        <template v-if="periodMode === 'range'">
          <v-text-field
            v-model="rangeFrom"
            type="date"
            label="From"
            dense
            outlined
            hide-details
            style="max-width: 170px"
          />
          <v-text-field
            v-model="rangeTo"
            type="date"
            label="To"
            dense
            outlined
            hide-details
            style="max-width: 170px"
          />
        </template>
      </div>

      <!-- Summary (reflects the active filters + period) -->
      <v-card outlined class="mb-4 fo-summary">
        <div class="pa-4">
          <!-- Headline KPIs -->
          <v-row align="stretch" no-gutters>
            <v-col cols="6" md="3" class="fo-kpi">
              <v-icon color="indigo lighten-1" class="mb-1"
                >mdi-clipboard-list-outline</v-icon
              >
              <div class="fo-kpi-value">{{ metrics.total }}</div>
              <div class="fo-kpi-label">Total Field Orders</div>
            </v-col>
            <v-col cols="6" md="3" class="fo-kpi">
              <v-icon color="green" class="mb-1"
                >mdi-check-circle-outline</v-icon
              >
              <div class="fo-kpi-value green--text text--darken-1">
                {{ metrics.completed }}
              </div>
              <div class="fo-kpi-label">Completed</div>
            </v-col>
            <v-col cols="6" md="3" class="fo-kpi">
              <v-icon color="teal" class="mb-1">mdi-clock-check-outline</v-icon>
              <div class="fo-kpi-value teal--text text--darken-1">
                {{ metrics.onTimeRate }}%
              </div>
              <div class="fo-kpi-label">On Time</div>
              <div class="fo-kpi-sub">
                {{ metrics.completedOnTime }} of {{ metrics.completed }} completed
              </div>
            </v-col>
            <v-col cols="6" md="3" class="fo-kpi">
              <v-icon color="primary" class="mb-1">mdi-chart-donut</v-icon>
              <div class="fo-kpi-value primary--text">
                {{ metrics.completionRate }}%
              </div>
              <div class="fo-kpi-label">Completion Rate</div>
              <v-progress-linear
                :value="metrics.completionRate"
                color="primary"
                height="6"
                rounded
                background-color="grey lighten-3"
                class="mt-2 mx-auto"
                style="max-width: 120px"
              />
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- Status distribution -->
          <div class="d-flex align-center mb-2">
            <span class="fo-section-label">Status Breakdown</span>
            <v-spacer />
            <span class="caption text--secondary">{{ metrics.total }} total</span>
          </div>
          <div
            class="d-flex mb-3"
            style="
              width: 100%;
              height: 10px;
              border-radius: 6px;
              overflow: hidden;
              background: #f0f0f0;
            "
          >
            <div
              v-for="seg in statusBreakdown"
              :key="seg.key"
              v-show="seg.count > 0"
              :style="{ width: seg.pct + '%', background: seg.color }"
              style="height: 100%"
              :title="seg.label + ': ' + seg.count"
            ></div>
          </div>
          <div class="d-flex flex-wrap">
            <div
              v-for="seg in statusBreakdown"
              :key="seg.key"
              class="d-flex align-center mr-6 mb-1"
            >
              <v-icon :color="seg.color" size="12" class="mr-2"
                >mdi-circle</v-icon
              >
              <span class="text--secondary mr-2">{{ seg.label }}</span>
              <span class="font-weight-bold">{{ seg.count }}</span>
            </div>
          </div>
        </div>
      </v-card>

      <!-- Filters -->
      <v-row class="mb-2" dense>
        <v-col cols="12" sm="3">
          <v-select
            v-model="filterBranchId"
            :items="branchFilterItems"
            label="Branch"
            dense
            outlined
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-select
            v-model="filterJobType"
            :items="jobTypeFilterItems"
            label="Job Type"
            dense
            outlined
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-select
            v-model="filterOrderSource"
            :items="orderSourceFilterItems"
            label="Order Source"
            dense
            outlined
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-select
            v-model="filterMunicipality"
            :items="municipalityFilterItems"
            label="Municipality"
            dense
            outlined
            hide-details
          />
        </v-col>
      </v-row>

      <!-- Tabs -->
      <v-tabs v-model="activeTab" class="mb-3">
        <v-tab>
          Pending
          <v-badge
            v-if="tabOrders('pending').length"
            :content="tabOrders('pending').length"
            color="orange"
            class="ml-2"
            inline
          />
        </v-tab>
        <v-tab>
          Ongoing
          <v-badge
            v-if="tabOrders('ongoing').length"
            :content="tabOrders('ongoing').length"
            color="blue"
            class="ml-2"
            inline
          />
        </v-tab>
        <v-tab>
          Completed
          <v-badge
            v-if="tabOrders('completed').length"
            :content="tabOrders('completed').length"
            color="green"
            class="ml-2"
            inline
          />
        </v-tab>
        <v-tab>
          Cancelled
          <v-badge
            v-if="tabOrders('cancelled').length"
            :content="tabOrders('cancelled').length"
            color="red"
            class="ml-2"
            inline
          />
        </v-tab>
        <v-tab>
          All
          <v-badge
            v-if="filteredOrders.length"
            :content="filteredOrders.length"
            color="grey"
            class="ml-2"
            inline
          />
        </v-tab>
        <v-tab>
          <v-icon small class="mr-1">mdi-calendar-month</v-icon>
          Calendar
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item v-for="tab in tabs" :key="tab.value">
          <v-data-table
            :items="tabOrders(tab.value)"
            :headers="headers"
            :loading="loading"
            dense
          >
            <template v-slot:item.job_type="{ item }">
              <v-chip small dark :color="chipColor(item)">
                <v-icon
                  v-if="item.status === 'completed'"
                  left
                  x-small
                  >mdi-check-circle</v-icon
                >
                {{ item.job_type
                }}<span v-if="item.status === 'completed'" class="ml-1"
                  >· Completed</span
                >
              </v-chip>
              <v-chip
                v-if="item.maintenance_visit"
                x-small
                outlined
                color="deep-purple"
                class="ml-1"
                title="Maintenance visit number in the free year"
              >
                visit {{ item.maintenance_visit.visit_number }} of 4
              </v-chip>
            </template>
            <template v-slot:item.description="{ item }">
              {{ truncate(item.description, 60) }}
            </template>
            <template v-slot:item.scheduled_date="{ item }">
              {{ scheduleLabel(item) }}
              <v-chip
                v-if="durationOf(item) > 1"
                x-small
                outlined
                color="indigo"
                class="ml-1"
                >{{ durationOf(item) }} days</v-chip
              >
              <v-chip
                v-if="isRescheduled(item)"
                x-small
                color="amber darken-2"
                dark
                class="ml-1"
                :title="rescheduleTooltip(item)"
              >
                <v-icon x-small left>mdi-calendar-sync</v-icon>
                Rescheduled
              </v-chip>
            </template>
            <template v-slot:item.branch="{ item }">
              {{ item.branch ? item.branch.branch_name : "" }}
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip :color="statusColor(item.status)" dark small>
                {{ statusLabel(item.status) }}
              </v-chip>
            </template>
            <template v-slot:item.action="{ item }">
              <v-btn
                icon
                small
                @click="openStatusDialog(item)"
                title="Update Status"
              >
                <v-icon small color="primary"
                  >mdi-clipboard-edit-outline</v-icon
                >
              </v-btn>
              <v-btn
                icon
                small
                @click="openRescheduleDialog(item)"
                title="Reschedule"
              >
                <v-icon small color="amber darken-2"
                  >mdi-calendar-sync</v-icon
                >
              </v-btn>
              <v-btn
                icon
                small
                @click="openHistoryDialog(item)"
                title="History"
              >
                <v-icon small color="grey darken-1">mdi-history</v-icon>
              </v-btn>
              <v-btn icon small @click="openOrderDialog(item)" title="Edit">
                <v-icon small color="orange darken-1">mdi-pencil</v-icon>
              </v-btn>
            </template>
            <template v-slot:no-data>
              <div class="pa-4 grey--text">No field orders found.</div>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- Calendar view: click a day (or an event) to see that day's orders -->
        <v-tab-item>
          <div class="d-flex align-center mb-2">
            <v-btn icon small @click="$refs.fjoCalendar.prev()">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn small text class="mx-1" @click="calendarFocus = todayStr">
              Today
            </v-btn>
            <v-btn icon small @click="$refs.fjoCalendar.next()">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <span class="subtitle-1 ml-2">{{ calendarTitle }}</span>
          </div>
          <v-calendar
            ref="fjoCalendar"
            v-model="calendarFocus"
            type="month"
            :events="calendarEvents"
            :event-color="(event) => event.color"
            @click:day="openDayDialog"
            @click:event="clickEvent"
            @click:more="openDayDialog"
          >
            <template v-slot:event="{ event }">
              <v-chip
                :color="event.color"
                :dark="!event.ghost"
                :outlined="event.ghost"
                x-small
                label
                class="ma-0 mb-1 px-1 d-block text-truncate"
                :class="{ 'fo-ghost-chip': event.ghost }"
              >
                <v-icon v-if="event.ghost" x-small left
                  >mdi-calendar-remove</v-icon
                >
                <v-icon v-else-if="event.rescheduled" x-small left
                  >mdi-calendar-sync</v-icon
                >
                {{ event.name }}
              </v-chip>
            </template>
          </v-calendar>
        </v-tab-item>
      </v-tabs-items>

      <!-- Day Orders Dialog (from calendar) -->
      <v-dialog v-model="dayDialog" max-width="500">
        <v-card>
          <v-card-title>
            {{ formatDate(dayDate) }}
            <v-spacer />
            <v-btn
              small
              color="primary"
              class="mr-1"
              @click="openNewOrderForDay"
            >
              <v-icon left small>mdi-plus</v-icon>
              New Field Order
            </v-btn>
            <v-btn icon @click="dayDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-list dense v-if="dayOrders.length">
              <v-list-item v-for="order in dayOrders" :key="order.id">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ order.customer_name }}
                    <v-chip small dark :color="chipColor(order)" class="ml-1">
                      <v-icon
                        v-if="order.status === 'completed'"
                        left
                        x-small
                        >mdi-check-circle</v-icon
                      >
                      {{ order.job_type
                      }}<span v-if="order.status === 'completed'" class="ml-1"
                        >· Completed</span
                      >
                    </v-chip>
                  </v-list-item-title>
                  <div
                    class="body-2 text--secondary"
                    style="white-space: normal; word-break: break-word"
                  >
                    {{ order.description }}
                  </div>
                  <v-list-item-subtitle class="caption">
                    {{ order.order_source }} &middot;
                    {{ order.branch ? order.branch.branch_name : "" }} &middot;
                    Inserted by: {{ order.inserted_by_hats }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="flex-row align-center">
                  <v-chip
                    v-if="isRescheduled(order)"
                    color="amber darken-2"
                    dark
                    x-small
                    class="mr-1"
                    :title="rescheduleTooltip(order)"
                  >
                    <v-icon x-small left>mdi-calendar-sync</v-icon>
                    Rescheduled
                  </v-chip>
                  <v-chip
                    :color="statusColor(order.status)"
                    dark
                    x-small
                    class="mr-1"
                  >
                    {{ statusLabel(order.status) }}
                  </v-chip>
                  <v-btn
                    icon
                    small
                    @click="openStatusDialog(order)"
                    title="Update Status"
                  >
                    <v-icon small color="primary"
                      >mdi-clipboard-edit-outline</v-icon
                    >
                  </v-btn>
                  <v-btn
                    icon
                    small
                    @click="openRescheduleDialog(order)"
                    title="Reschedule"
                  >
                    <v-icon small color="amber darken-2"
                      >mdi-calendar-sync</v-icon
                    >
                  </v-btn>
                  <v-btn
                    icon
                    small
                    @click="openHistoryDialog(order)"
                    title="History"
                  >
                    <v-icon small color="grey darken-1">mdi-history</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    @click="openOrderDialog(order)"
                    title="Edit"
                  >
                    <v-icon small color="orange darken-1">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    @click="confirmDeleteOrder(order)"
                    title="Delete"
                  >
                    <v-icon small color="red darken-1">mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <div v-else class="text-center grey--text pa-4">
              No field orders scheduled on this day.
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- New / Edit Order Dialog -->
      <v-dialog v-model="orderDialog" max-width="500" persistent>
        <v-card>
          <v-card-title>
            {{ editingOrder ? "Edit Field Order" : "New Field Order" }}
          </v-card-title>
          <v-card-text>
            <v-text-field
              v-model="orderForm.customer_name"
              label="Customer Name"
              outlined
              dense
            />
            <v-select
              v-model="orderForm.job_type"
              :items="jobTypes"
              label="Job Type"
              outlined
              dense
            />
            <v-select
              v-model="orderForm.order_source"
              :items="orderSources"
              label="Order Source"
              outlined
              dense
            />
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="orderForm.scheduled_date"
                  label="Scheduled Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  dense
                  outlined
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="orderForm.scheduled_date"
                @input="dateMenu = false"
              ></v-date-picker>
            </v-menu>
            <v-text-field
              v-model.number="orderForm.duration_days"
              label="Completion Span (days)"
              type="number"
              min="1"
              outlined
              dense
              prepend-icon="mdi-calendar-range"
              hint="How many days the job spans on the calendar; also the on-time deadline."
              persistent-hint
              class="mb-2"
            />
            <div v-if="orderForm.scheduled_date" class="caption text--secondary mb-3">
              Spans {{ formatDate(orderForm.scheduled_date) }}
              <template v-if="(orderForm.duration_days || 1) > 1">
                &rarr;
                {{
                  formatDate(
                    spanEndDate(
                      orderForm.scheduled_date,
                      orderForm.duration_days
                    )
                  )
                }}
              </template>
            </div>
            <v-select
              v-model="orderForm.branch_id"
              :items="branchItems"
              label="Branch"
              outlined
              dense
            />
            <v-textarea
              v-model="orderForm.description"
              label="Description"
              outlined
              dense
              rows="3"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="orderDialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="saveOrder" :loading="saving">
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Update Status Dialog -->
      <v-dialog v-model="statusDialog" max-width="400">
        <v-card>
          <v-card-title>Update Status</v-card-title>
          <v-card-subtitle v-if="selectedOrder">{{
            selectedOrder.customer_name
          }}</v-card-subtitle>
          <v-card-text>
            <v-select
              v-model="statusForm.status"
              :items="statuses"
              label="Status"
              outlined
              dense
            />
            <v-textarea
              v-model="statusForm.remark"
              label="Remark (optional)"
              outlined
              dense
              rows="3"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="statusDialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="saveStatus" :loading="saving">
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Reschedule Dialog -->
      <v-dialog v-model="rescheduleDialog" max-width="420" persistent>
        <v-card>
          <v-card-title>Reschedule Field Order</v-card-title>
          <v-card-subtitle v-if="selectedOrder">
            {{ selectedOrder.customer_name }} &mdash; currently
            {{ formatDate(selectedOrder.scheduled_date) }}
          </v-card-subtitle>
          <v-card-text>
            <div class="caption text--secondary mb-3">
              The original date stays on the calendar as a "moved" chip; a new
              chip is added on the new date.
            </div>
            <v-menu
              v-model="rescheduleMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="rescheduleForm.scheduled_date"
                  label="New Scheduled Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  dense
                  outlined
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="rescheduleForm.scheduled_date"
                @input="rescheduleMenu = false"
              ></v-date-picker>
            </v-menu>
            <v-textarea
              v-model="rescheduleForm.remark"
              label="Reason (optional)"
              outlined
              dense
              rows="2"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="rescheduleDialog = false">Cancel</v-btn>
            <v-btn
              color="amber darken-2"
              dark
              @click="saveReschedule"
              :loading="rescheduleSaving"
            >
              Reschedule
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- History Dialog -->
      <v-dialog v-model="historyDialog" max-width="500">
        <v-card>
          <v-card-title>
            Status History
            <v-spacer />
            <v-btn icon @click="historyDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-subtitle v-if="selectedOrder">{{
            selectedOrder.customer_name
          }}</v-card-subtitle>
          <v-card-text>
            <v-list dense v-if="selectedOrderLogs.length">
              <v-list-item
                v-for="log in selectedOrderLogs"
                :key="log.id"
                class="mb-1"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <v-chip :color="statusColor(log.status)" dark x-small>
                      {{ statusLabel(log.status) }}
                    </v-chip>
                    <span v-if="log.remark" class="ml-2">{{ log.remark }}</span>
                  </v-list-item-title>
                  <v-list-item-subtitle class="caption">
                    {{ log.changed_by ? log.changed_by.full_name : "" }}
                    &mdash; {{ formatDateTime(log.created_at) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <div v-else class="text-center grey--text pa-4">
              {{ historyLoading ? "Loading..." : "No history yet." }}
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Manage Job Types Dialog -->
      <v-dialog v-model="typesDialog" max-width="560" scrollable>
        <v-card>
          <v-card-title>
            Manage Job Types
            <v-spacer />
            <v-btn icon @click="typesDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <!-- Add / Edit form -->
            <v-row dense align="center">
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="typeForm.name"
                  label="Type Name"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="8" sm="4">
                <v-menu
                  v-model="colorMenu"
                  :close-on-content-click="false"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="typeForm.color"
                      label="Color"
                      outlined
                      dense
                      hide-details
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    >
                      <template v-slot:prepend-inner>
                        <v-avatar :color="typeForm.color" size="20" />
                      </template>
                    </v-text-field>
                  </template>
                  <v-color-picker
                    v-model="typeForm.color"
                    mode="hex"
                    hide-mode-switch
                  />
                </v-menu>
              </v-col>
              <v-col cols="4" sm="3">
                <v-btn
                  color="primary"
                  block
                  :loading="typeSaving"
                  @click="saveType"
                >
                  {{ editingType ? "Update" : "Add" }}
                </v-btn>
              </v-col>
            </v-row>
            <div v-if="editingType" class="text-right mb-2">
              <v-btn x-small text @click="resetTypeForm">Cancel edit</v-btn>
            </div>

            <v-divider class="my-3" />

            <v-list dense>
              <v-list-item
                v-for="t in allJobTypes"
                :key="t.id"
                :class="{ 'text--disabled': !t.is_active }"
              >
                <v-list-item-avatar size="24">
                  <v-avatar :color="t.color" size="24" />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ t.name }}
                    <v-chip
                      v-if="!t.is_active"
                      x-small
                      class="ml-1"
                      color="grey lighten-1"
                      >inactive</v-chip
                    >
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="flex-row align-center">
                  <v-btn
                    icon
                    small
                    :title="t.is_active ? 'Deactivate' : 'Activate'"
                    @click="toggleTypeActive(t)"
                  >
                    <v-icon small>{{
                      t.is_active ? "mdi-eye-off" : "mdi-eye"
                    }}</v-icon>
                  </v-btn>
                  <v-btn icon small title="Edit" @click="editType(t)">
                    <v-icon small color="orange darken-1">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small title="Delete" @click="deleteType(t)">
                    <v-icon small color="red darken-1">mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-dialog>

      <p class="caption grey--text text-right">fieldJobOrders.vue</p>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
export default {
  data() {
    return {
      saving: false,
      // Calendar is the last tab (after Pending/Ongoing/Completed/Cancelled/All)
      activeTab: 5,
      filterBranchId: null,
      filterJobType: null,
      filterOrderSource: null,
      filterMunicipality: null,
      // Period filter on scheduled_date: all | week (Mon–Sun) | month | range
      periodMode: "all",
      periodAnchor: null,
      rangeFrom: "",
      rangeTo: "",
      orderDialog: false,
      statusDialog: false,
      historyDialog: false,
      rescheduleDialog: false,
      rescheduleMenu: false,
      rescheduleSaving: false,
      rescheduleForm: { scheduled_date: null, remark: "" },
      historyLoading: false,
      dateMenu: false,
      editingOrder: null,
      selectedOrder: null,
      orderForm: {
        customer_name: "",
        job_type: null,
        order_source: null,
        scheduled_date: null,
        duration_days: 1,
        branch_id: null,
        description: "",
      },
      statusForm: { status: null, remark: "" },
      // Manage Job Types dialog
      typesDialog: false,
      colorMenu: false,
      typeSaving: false,
      editingType: null,
      typeForm: { name: "", color: "#1976D2" },
      // Inactive-inclusive list, loaded when the manage dialog opens
      allJobTypes: [],
      calendarFocus: "",
      dayDialog: false,
      dayDate: null,
      orderSources: ["Walk-in", "Government PO", "Private PO"],
      statuses: [
        { text: "Pending", value: "pending" },
        { text: "Ongoing", value: "ongoing" },
        { text: "Completed", value: "completed" },
        { text: "Cancelled", value: "cancelled" },
      ],
      tabs: [
        { text: "Pending", value: "pending" },
        { text: "Ongoing", value: "ongoing" },
        { text: "Completed", value: "completed" },
        { text: "Cancelled", value: "cancelled" },
        { text: "All", value: "all" },
      ],
      headers: [
        { text: "Customer", value: "customer_name" },
        { text: "Job Type", value: "job_type" },
        { text: "Order Source", value: "order_source" },
        { text: "Description", value: "description", sortable: false },
        { text: "Scheduled", value: "scheduled_date" },
        { text: "Branch", value: "branch" },
        { text: "Inserted By", value: "inserted_by_hats" },
        { text: "Status", value: "status" },
        { text: "Actions", value: "action", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters({
      orders: "field_job_order/orders",
      loading: "field_job_order/loading",
      status_counts: "field_job_order/status_counts",
      order_logs: "field_job_order/order_logs",
      job_types_catalog: "field_job_order/job_types",
      branches: "branch/branches",
      user: "auth/user",
    }),
    // Active job-type names, for the order dialog + filter selects
    jobTypes() {
      return this.job_types_catalog
        .filter((t) => t.is_active)
        .map((t) => t.name);
    },
    // name -> hex color (includes inactive so historical rows keep their color)
    jobTypeColorMap() {
      var map = {};
      this.job_types_catalog.forEach((t) => {
        map[t.name] = t.color;
      });
      return map;
    },
    // Performance figures over the filtered orders in the selected period
    metrics() {
      var total = this.periodOrders.length;
      var completed = this.periodOrders.filter(
        (o) => o.status === "completed"
      );
      var onTime = completed.filter((o) => this.isOnTime(o));
      return {
        total: total,
        completed: completed.length,
        completedOnTime: onTime.length,
        onTimeRate: completed.length
          ? Math.round((onTime.length / completed.length) * 100)
          : 0,
        completionRate: total
          ? Math.round((completed.length / total) * 100)
          : 0,
      };
    },
    // { from, to } (YYYY-MM-DD, inclusive) for the selected period; null = all
    periodBounds() {
      if (this.periodMode === "range") {
        if (!this.rangeFrom && !this.rangeTo) return null;
        return { from: this.rangeFrom || "", to: this.rangeTo || "9999-12-31" };
      }
      if (this.periodMode !== "week" && this.periodMode !== "month")
        return null;
      var anchor = new Date((this.periodAnchor || this.todayYmd()) + "T00:00:00");
      var from, to;
      if (this.periodMode === "week") {
        // Week runs Monday–Sunday
        from = new Date(anchor);
        from.setDate(anchor.getDate() - ((anchor.getDay() + 6) % 7));
        to = new Date(from);
        to.setDate(from.getDate() + 6);
      } else {
        from = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        to = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
      }
      return { from: this.toYmd(from), to: this.toYmd(to) };
    },
    periodLabel() {
      var b = this.periodBounds;
      if (!b) return "";
      if (this.periodMode === "month") {
        return new Date(b.from + "T00:00:00").toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
      }
      return this.formatDate(b.from) + " – " + this.formatDate(b.to);
    },
    // filteredOrders narrowed to the period (by scheduled date)
    periodOrders() {
      var b = this.periodBounds;
      if (!b) return this.filteredOrders;
      return this.filteredOrders.filter((o) => {
        var d = this.dateOnly(o.scheduled_date);
        return d >= b.from && d <= b.to;
      });
    },
    // Per-status distribution over the period orders (for the summary bar).
    statusBreakdown() {
      var counts = { pending: 0, ongoing: 0, completed: 0, cancelled: 0 };
      this.periodOrders.forEach((o) => {
        if (counts[o.status] !== undefined) counts[o.status]++;
      });
      var total = this.periodOrders.length;
      var defs = [
        { key: "pending", label: "Pending", color: "#F9A825" },
        { key: "ongoing", label: "Ongoing", color: "#1E88E5" },
        { key: "completed", label: "Completed", color: "#43A047" },
        { key: "cancelled", label: "Cancelled", color: "#E53935" },
      ];
      return defs.map((d) => ({
        key: d.key,
        label: d.label,
        color: d.color,
        count: counts[d.key],
        pct: total ? (counts[d.key] / total) * 100 : 0,
      }));
    },
    branchItems() {
      return this.branches.map((b) => ({
        text: b.branch_name,
        value: b.id,
      }));
    },
    branchFilterItems() {
      return [{ text: "All Branches", value: null }].concat(this.branchItems);
    },
    jobTypeFilterItems() {
      return [{ text: "All Job Types", value: null }].concat(
        this.jobTypes.map((t) => ({ text: t, value: t }))
      );
    },
    orderSourceFilterItems() {
      return [{ text: "All Sources", value: null }].concat(
        this.orderSources.map((s) => ({ text: s, value: s }))
      );
    },
    municipalityFilterItems() {
      // Derived from the municipality of each order's linked installed system
      // (maintenance jobs). This is what makes batching visible: filter to
      // Maintenance + a municipality and move those jobs onto one day.
      var set = new Set(
        this.orders
          .map((o) => o.installed_system && o.installed_system.site_municipality)
          .filter(Boolean)
      );
      return [{ text: "All Municipalities", value: null }].concat(
        Array.from(set)
          .sort()
          .map((m) => ({ text: m, value: m }))
      );
    },
    filteredOrders() {
      return this.orders.filter((o) => {
        if (this.filterBranchId && o.branch_id != this.filterBranchId)
          return false;
        if (this.filterJobType && o.job_type != this.filterJobType)
          return false;
        if (this.filterOrderSource && o.order_source != this.filterOrderSource)
          return false;
        if (
          this.filterMunicipality &&
          (!o.installed_system ||
            o.installed_system.site_municipality != this.filterMunicipality)
        )
          return false;
        return true;
      });
    },
    selectedOrderLogs() {
      if (!this.selectedOrder) return [];
      // Show newest first; server returns oldest first
      return (this.order_logs[this.selectedOrder.id] || []).slice().reverse();
    },
    todayStr() {
      var d = new Date();
      return (
        d.getFullYear() +
        "-" +
        String(d.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(d.getDate()).padStart(2, "0")
      );
    },
    calendarEvents() {
      var events = [];
      this.filteredOrders.forEach((o) => {
        var rescheduled = this.isRescheduled(o);
        // Live chip on the current scheduled date
        events.push({
          name: o.customer_name + " (" + o.job_type + ")",
          start: this.dateOnly(o.scheduled_date),
          // Multi-day span: scheduled_date .. scheduled_date + duration - 1
          end: this.spanEndDate(o.scheduled_date, this.durationOf(o)),
          // Colour by job type; completed orders turn green (see chipColor)
          color: this.chipColor(o),
          timed: false,
          ghost: false,
          rescheduled: rescheduled,
          order: o,
        });
        // Ghost chip left behind on each original date
        this.originalDates(o).forEach((start) => {
          events.push({
            name: o.customer_name + " (moved)",
            start: start,
            end: this.spanEndDate(start, this.durationOf(o)),
            color: "#BDBDBD",
            timed: false,
            ghost: true,
            order: o,
          });
        });
      });
      return events;
    },
    calendarTitle() {
      if (!this.calendarFocus) return "";
      var d = new Date(this.calendarFocus + "T00:00:00");
      return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    },
    dayOrders() {
      if (!this.dayDate) return [];
      return this.filteredOrders.filter((o) =>
        this.orderCoversDay(o, this.dayDate)
      );
    },
  },
  methods: {
    ...mapActions({
      fetch_orders: "field_job_order/fetch_orders",
      create_order: "field_job_order/create_order",
      update_order: "field_job_order/update_order",
      update_order_status: "field_job_order/update_order_status",
      fetch_order_logs: "field_job_order/fetch_order_logs",
      reschedule_order: "field_job_order/reschedule_order",
      delete_order: "field_job_order/delete_order",
      fetch_job_types: "field_job_order/fetch_job_types",
      create_job_type: "field_job_order/create_job_type",
      update_job_type: "field_job_order/update_job_type",
      delete_job_type: "field_job_order/delete_job_type",
      get_branches: "branch/get_branches",
    }),
    // ---- Job type colour / span helpers ----
    jobTypeColor(name) {
      return this.jobTypeColorMap[name] || "#757575";
    },
    // Completed orders turn the job-type chip green (with a check tag);
    // otherwise it keeps the job type's own colour.
    chipColor(order) {
      return order && order.status === "completed"
        ? "#43A047"
        : this.jobTypeColor(order && order.job_type);
    },
    // ---- Reschedule helpers ----
    isRescheduled(order) {
      return !!(
        order &&
        order.reschedule_history &&
        order.reschedule_history.length
      );
    },
    // The vacated original date(s) a ghost chip should stay on.
    originalDates(order) {
      if (!this.isRescheduled(order)) return [];
      return order.reschedule_history
        .map((h) => this.dateOnly(h.from))
        .filter(Boolean);
    },
    // Does the order touch this day via its current span OR any original span?
    orderCoversDay(order, day) {
      var dur = this.durationOf(order);
      var spans = [this.dateOnly(order.scheduled_date)].concat(
        this.originalDates(order)
      );
      return spans.some((start) => {
        if (!start) return false;
        var end = this.spanEndDate(start, dur);
        return start <= day && day <= end;
      });
    },
    rescheduleTooltip(order) {
      if (!this.isRescheduled(order)) return "";
      return (
        "Rescheduled " +
        order.reschedule_history.length +
        "×: " +
        order.reschedule_history
          .map((h) => this.formatDate(h.from) + " → " + this.formatDate(h.to))
          .join(", ")
      );
    },
    openRescheduleDialog(order) {
      this.selectedOrder = order;
      this.rescheduleForm = {
        scheduled_date: this.dateOnly(order.scheduled_date),
        remark: "",
      };
      this.rescheduleDialog = true;
    },
    async saveReschedule() {
      var f = this.rescheduleForm;
      if (!f.scheduled_date) {
        this.$alert({
          title: "Validation",
          message: "Please choose a new date.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      if (
        this.dateOnly(this.selectedOrder.scheduled_date) === f.scheduled_date
      ) {
        this.$alert({
          title: "Validation",
          message: "The new date must be different from the current one.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      this.rescheduleSaving = true;
      try {
        await this.reschedule_order({
          id: this.selectedOrder.id,
          scheduled_date: f.scheduled_date,
          remark: f.remark,
        });
        this.rescheduleDialog = false;
        this.$alert({
          title: "Success",
          message: "Field order rescheduled.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response?.data?.message || e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.rescheduleSaving = false;
    },
    durationOf(order) {
      var d = parseInt(order && order.duration_days, 10);
      return d && d > 0 ? d : 1;
    },
    spanEndDate(startStr, durationDays) {
      var start = this.dateOnly(startStr);
      if (!start) return start;
      var d = parseInt(durationDays, 10);
      if (!d || d < 1) d = 1;
      var dt = new Date(start + "T00:00:00");
      dt.setDate(dt.getDate() + (d - 1));
      return (
        dt.getFullYear() +
        "-" +
        String(dt.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(dt.getDate()).padStart(2, "0")
      );
    },
    scheduleLabel(order) {
      var start = this.dateOnly(order.scheduled_date);
      var end = this.spanEndDate(start, this.durationOf(order));
      if (end === start) return this.formatDate(start);
      return this.formatDate(start) + " – " + this.formatDate(end);
    },
    // "On time" = completed on or before the last day of the planned span.
    isOnTime(order) {
      if (order.status !== "completed" || !order.completed_at) return false;
      var end = this.spanEndDate(
        order.scheduled_date,
        this.durationOf(order)
      );
      return this.dateOnly(order.completed_at) <= end;
    },
    tabOrders(status) {
      if (status === "all") return this.periodOrders;
      return this.periodOrders.filter((o) => o.status === status);
    },
    toYmd(dt) {
      return (
        dt.getFullYear() +
        "-" +
        String(dt.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(dt.getDate()).padStart(2, "0")
      );
    },
    todayYmd() {
      return this.toYmd(new Date());
    },
    // Move the week/month period back (-1) or forward (+1)
    shiftPeriod(dir) {
      var d = new Date((this.periodAnchor || this.todayYmd()) + "T00:00:00");
      if (this.periodMode === "week") d.setDate(d.getDate() + 7 * dir);
      else d = new Date(d.getFullYear(), d.getMonth() + dir, 1);
      this.periodAnchor = this.toYmd(d);
    },
    dateOnly(dateStr) {
      if (!dateStr) return "";
      return String(dateStr).substr(0, 10);
    },
    openDayDialog(day) {
      // v-calendar passes { date: "YYYY-MM-DD" } for click:day / click:more
      this.dayDate = day && day.date ? day.date : null;
      if (!this.dayDate) return;
      this.dayDialog = true;
    },
    clickEvent(payload) {
      var event = payload && payload.event;
      if (event && event.order) {
        this.dayDate = this.dateOnly(event.order.scheduled_date);
        this.dayDialog = true;
      }
    },
    openNewOrderForDay() {
      // Pre-fill the scheduled date with the calendar day that was clicked
      this.openOrderDialog();
      this.orderForm.scheduled_date = this.dayDate;
      this.dayDialog = false;
    },
    statusColor(status) {
      switch (status) {
        case "pending":
          return "orange";
        case "ongoing":
          return "blue";
        case "completed":
          return "green";
        case "cancelled":
          return "red";
        default:
          return "grey";
      }
    },
    statusLabel(status) {
      if (!status) return "";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    truncate(str, maxLen) {
      if (!str) return "";
      return str.length > maxLen ? str.substring(0, maxLen) + "..." : str;
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      var d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "";
      var d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      return (
        this.formatDate(dateStr) +
        " " +
        d.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
        })
      );
    },
    async refreshAll() {
      try {
        await this.fetch_orders();
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            "Error loading field orders: " +
            (e.response?.data?.message || e.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    openOrderDialog(order) {
      this.editingOrder = order || null;
      if (order) {
        this.orderForm = {
          customer_name: order.customer_name,
          job_type: order.job_type,
          order_source: order.order_source,
          scheduled_date: order.scheduled_date,
          duration_days: this.durationOf(order),
          branch_id: order.branch_id,
          description: order.description,
        };
      } else {
        this.orderForm = {
          customer_name: "",
          job_type: null,
          order_source: null,
          scheduled_date: null,
          duration_days: 1,
          branch_id: this.user ? this.user.branch_id : null,
          description: "",
        };
      }
      this.orderDialog = true;
    },
    async saveOrder() {
      var f = this.orderForm;
      if (
        !f.customer_name ||
        !f.job_type ||
        !f.order_source ||
        !f.scheduled_date ||
        !f.description
      ) {
        this.$alert({
          title: "Validation",
          message: "Please fill in all required fields.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      // Normalise the span to a whole number >= 1
      var dur = parseInt(f.duration_days, 10);
      f.duration_days = dur && dur > 0 ? dur : 1;
      this.saving = true;
      try {
        if (this.editingOrder) {
          await this.update_order({ id: this.editingOrder.id, ...f });
        } else {
          await this.create_order(f);
        }
        this.orderDialog = false;
        this.$alert({
          title: "Success",
          message: this.editingOrder
            ? "Field order updated."
            : "Field order created.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response?.data?.message || e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.saving = false;
    },
    openStatusDialog(order) {
      this.selectedOrder = order;
      this.statusForm = { status: order.status, remark: "" };
      this.statusDialog = true;
    },
    async saveStatus() {
      if (!this.statusForm.status) {
        this.$alert({
          title: "Validation",
          message: "Please select a status.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      this.saving = true;
      try {
        await this.update_order_status({
          id: this.selectedOrder.id,
          status: this.statusForm.status,
          remark: this.statusForm.remark,
        });
        this.statusDialog = false;
        // Refresh the logs map for this order
        this.fetch_order_logs(this.selectedOrder.id).catch(() => {});
        this.$alert({
          title: "Success",
          message: "Status updated.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response?.data?.message || e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.saving = false;
    },
    async openHistoryDialog(order) {
      this.selectedOrder = order;
      this.historyDialog = true;
      this.historyLoading = true;
      try {
        await this.fetch_order_logs(order.id);
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response?.data?.message || e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.historyLoading = false;
    },
    async confirmDeleteOrder(order) {
      const confirmed = await this.$confirm({
        title: "Delete Field Order",
        message:
          "Are you sure you want to delete the field order for " +
          order.customer_name +
          "?",
        color: "red",
        icon: "mdi-delete",
      });
      if (!confirmed) return;
      try {
        await this.delete_order(order.id);
        this.$alert({
          title: "Success",
          message: "Field order deleted.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response?.data?.message || e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },

    // ---- Job type management ----
    async loadTypes() {
      try {
        await this.fetch_job_types();
      } catch (e) {
        /* non-fatal: chips just fall back to grey */
      }
    },
    async openTypesDialog() {
      this.resetTypeForm();
      try {
        // Load the inactive-inclusive list for the manage dialog
        const res = await this.$store.dispatch(
          "field_job_order/fetch_job_types",
          true
        );
        this.allJobTypes = res || [];
      } catch (e) {
        this.allJobTypes = [];
      }
      this.typesDialog = true;
    },
    resetTypeForm() {
      this.editingType = null;
      this.typeForm = { name: "", color: "#1976D2" };
    },
    editType(t) {
      this.editingType = t;
      this.typeForm = { name: t.name, color: t.color };
    },
    async refreshTypes() {
      // Refresh both the active catalog (chips/selects) and the manage list
      await this.loadTypes();
      try {
        const res = await this.$store.dispatch(
          "field_job_order/fetch_job_types",
          true
        );
        this.allJobTypes = res || [];
      } catch (e) {
        /* ignore */
      }
    },
    async saveType() {
      if (!this.typeForm.name || !this.typeForm.color) {
        this.$alert({
          title: "Validation",
          message: "Name and color are required.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      this.typeSaving = true;
      try {
        if (this.editingType) {
          await this.update_job_type({
            id: this.editingType.id,
            name: this.typeForm.name,
            color: this.typeForm.color,
          });
        } else {
          await this.create_job_type({
            name: this.typeForm.name,
            color: this.typeForm.color,
          });
        }
        this.resetTypeForm();
        await this.refreshTypes();
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response?.data?.message || e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.typeSaving = false;
    },
    async toggleTypeActive(t) {
      try {
        await this.update_job_type({
          id: t.id,
          name: t.name,
          color: t.color,
          is_active: !t.is_active,
        });
        await this.refreshTypes();
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response?.data?.message || e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    async deleteType(t) {
      const confirmed = await this.$confirm({
        title: "Delete Job Type",
        message: 'Delete the job type "' + t.name + '"?',
        color: "red",
        icon: "mdi-delete",
      });
      if (!confirmed) return;
      try {
        await this.delete_job_type(t.id);
        await this.refreshTypes();
      } catch (e) {
        this.$alert({
          title: "Cannot Delete",
          message: e.response?.data?.message || e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
  },
  async created() {
    this.calendarFocus = this.todayStr;
    if (this.branches.length <= 0) {
      this.get_branches().catch(() => {});
    }
    this.loadTypes();
    await this.refreshAll();
  },
};
</script>

<style scoped>
.fo-summary {
  border-radius: 12px;
}
/* Ghost chip left on an order's original date after a reschedule */
.fo-ghost-chip {
  border-style: dashed !important;
  opacity: 0.85;
  text-decoration: line-through;
}
.fo-kpi {
  text-align: center;
  padding: 8px 12px;
}
/* Subtle vertical separators between KPIs on wider screens */
@media (min-width: 960px) {
  .fo-kpi + .fo-kpi {
    border-left: 1px solid rgba(0, 0, 0, 0.06);
  }
}
.fo-kpi-value {
  font-size: 2rem;
  line-height: 1.1;
  font-weight: 700;
}
.fo-kpi-label {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 2px;
}
.fo-kpi-sub {
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.38);
}
.fo-section-label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.6);
}
/* Dark theme adjustments */
.theme--dark .fo-kpi + .fo-kpi {
  border-left-color: rgba(255, 255, 255, 0.08);
}
.theme--dark .fo-kpi-label,
.theme--dark .fo-section-label {
  color: rgba(255, 255, 255, 0.7);
}
.theme--dark .fo-kpi-sub {
  color: rgba(255, 255, 255, 0.5);
}
</style>
